import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  LinearProgress,
  Chip,
  IconButton,
  Avatar,
  Divider,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Alert,
  Dialog,
  DialogContent,
  Badge,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  DirectionsCar as DirectionsCarIcon,
  Place as PlaceIcon,
  LocalShipping as LocalShippingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Warehouse as WarehouseIcon,
} from "@mui/icons-material";

// Static placeholder images for demo
const DEMO_DELIVERY_IMAGES = [
  "https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55", // Package delivered
  "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600", // Delivery confirmation
  "https://images.unsplash.com/photo-1607827448387-a67db1383b59?w=400", // Signature/proof
];

// Mock data generator with stop-based structure
const generateMockData = (updateStopIndex = -1) => {
  const locations = [
    { name: "Thamel", area: "Kathmandu" },
    { name: "Boudha", area: "Kathmandu" },
    { name: "Patan", area: "Lalitpur" },
    { name: "Bhaktapur", area: "Bhaktapur" },
    { name: "Kirtipur", area: "Kathmandu" },
    { name: "Balaju", area: "Kathmandu" },
    { name: "Sankhamul", area: "Kathmandu" },
    { name: "Pulchowk", area: "Lalitpur" },
    { name: "Koteshwor", area: "Kathmandu" },
    { name: "Chabahil", area: "Kathmandu" },
  ];

  const customers = [
    "Ram Sharma",
    "Sita Poudel",
    "Hari Thapa",
    "Gita Maharjan",
    "Krishna Shrestha",
    "Laxmi Tamang",
    "Bikash Rai",
    "Anita Gurung",
    "Pramod Karki",
    "Sunita Shahi",
  ];

  let orderCounter = 1;

  const stops = locations.map((location, stopIdx) => {
    // Random 1-3 orders per stop
    const orderCount = Math.floor(Math.random() * 3) + 1;

    // Determine stop status based on position and update
    let stopStatus = "pending";
    if (stopIdx < 3) stopStatus = "completed";
    else if (stopIdx === 3)
      stopStatus = updateStopIndex === stopIdx ? "completed" : "completed";

    const orders = Array.from({ length: orderCount }, (_, orderIdx) => {
      const orderId = `ORD-${String(orderCounter++).padStart(3, "0")}`;

      let orderStatus = "loaded";
      if (stopStatus === "completed") {
        orderStatus = Math.random() > 0.1 ? "delivered" : "failed"; // 90% delivered, 10% failed
      }

      const timeline = [
        {
          status: "loaded",
          timestamp: new Date(
            Date.now() - (locations.length - stopIdx + 5) * 10 * 60000
          ).toISOString(),
        },
      ];

      if (orderStatus === "delivered") {
        timeline.push({
          status: "delivered",
          timestamp: new Date(
            Date.now() - (locations.length - stopIdx) * 7 * 60000
          ).toISOString(),
        });
      } else if (orderStatus === "failed") {
        timeline.push({
          status: "failed",
          timestamp: new Date(
            Date.now() - (locations.length - stopIdx) * 7 * 60000
          ).toISOString(),
        });
      }

      return {
        orderId,
        customerName: customers[Math.floor(Math.random() * customers.length)],
        phone: "+977-98########".replace(/#+/g, (m) =>
          String(Math.floor(Math.random() * Math.pow(10, m.length)))
        ),
        status: orderStatus,
        priority: Math.random() > 0.8 ? "urgent" : "normal",
        timeline,
        items: [`Auto Part ${orderId}-A`, `Auto Part ${orderId}-B`],
        notes: orderStatus === "failed" ? "Customer not available" : null,
      };
    });

    const stopTimeline = [
      {
        status: "pending",
        timestamp: new Date(
          Date.now() - (locations.length - stopIdx + 5) * 10 * 60000
        ).toISOString(),
      },
    ];

    if (stopStatus === "completed") {
      stopTimeline.push({
        status: "completed",
        timestamp: new Date(
          Date.now() - (locations.length - stopIdx) * 7 * 60000
        ).toISOString(),
      });
    }

    return {
      stopId: `STOP-${String(stopIdx + 1).padStart(2, "0")}`,
      stopNumber: stopIdx + 1,
      location: `${location.name}, ${location.area}`,
      address: `${location.name}, ${location.area}, Nepal`,
      status: stopStatus,
      orders,
      timeline: stopTimeline,
      estimatedArrival:
        stopStatus === "pending"
          ? new Date(Date.now() + (stopIdx - 5) * 15 * 60000).toISOString()
          : null,
    };
  });

  const totalOrders = stops.reduce((sum, stop) => sum + stop.orders.length, 0);
  const deliveredOrders = stops.reduce(
    (sum, stop) =>
      sum + stop.orders.filter((o) => o.status === "delivered").length,
    0
  );
  const completedStops = stops.filter((s) => s.status === "completed").length;
  const currentStop = stops.find((s) => s.status === "pending");

  return {
    driverId: "D123",
    driverName: "John Doe",
    driverPhone: "+977-9841234567",
    vehicleId: "BA-1-PA-1234",
    routeStartTime: new Date(Date.now() - 180 * 60000).toISOString(),
    totalStops: stops.length,
    completedStops,
    currentStop: currentStop ? currentStop.stopNumber : null,
    totalOrders,
    deliveredOrders,
    stops,
  };
};

const StopStatusChip = ({ status }) => {
  const configs = {
    pending: {
      color: "default",
      label: "Pending",
      icon: <AccessTimeIcon fontSize="small" />,
    },
    completed: {
      color: "success",
      label: "Completed",
      icon: <CheckCircleIcon fontSize="small" />,
    },
  };

  const config = configs[status] || configs.pending;

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      size="small"
      color={config.color}
      variant="outlined"
    />
  );
};

const OrderStatusChip = ({ status }) => {
  const configs = {
    loaded: { color: "default", label: "Pending" },
    delivered: { color: "success", label: "Delivered" },
    failed: { color: "error", label: "Failed" },
  };

  const config = configs[status] || configs.loaded;

  return (
    <Chip
      label={config.label}
      size="small"
      color={config.color}
      variant="outlined"
    />
  );
};

const OrderItem = ({ order, expanded }) => {
  const [showDetails, setShowDetails] = useState(expanded);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    setImageModalOpen(true);
  };

  const handleCloseModal = () => {
    setImageModalOpen(false);
  };

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) =>
      prev === 0 ? DEMO_DELIVERY_IMAGES.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) =>
      prev === DEMO_DELIVERY_IMAGES.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <Paper elevation={1} sx={{ mb: 1, borderRadius: 2 }}>
      <Box sx={{ p: 2 }}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item xs={8}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Typography variant="subtitle2" fontWeight="bold">
                  {order.orderId}
                </Typography>
              </Grid>
              {order.priority === "urgent" && (
                <Grid item>
                  <Chip
                    label="URGENT"
                    size="small"
                    color="error"
                    variant="filled"
                  />
                </Grid>
              )}
            </Grid>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {order.customerName}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{ mt: 0.5 }}
            >
              <PhoneIcon fontSize="small" />
              {order.phone}
            </Typography>
          </Grid>
          <Grid item>
            <OrderStatusChip status={order.status} />
          </Grid>
        </Grid>

        {showDetails && (
          <Collapse in={showDetails}>
            <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #eee" }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="caption"
                    fontWeight="medium"
                    color="text.secondary"
                  >
                    Items
                  </Typography>
                  <List dense>
                    {order.items.map((item, i) => (
                      <ListItem key={i} disableGutters>
                        <ListItemText
                          primary={`• ${item}`}
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="caption"
                    fontWeight="medium"
                    color="text.secondary"
                  >
                    Timeline
                  </Typography>
                  <List dense>
                    {order.timeline.map((event, i) => (
                      <ListItem key={i} disableGutters>
                        <ListItemIcon sx={{ minWidth: 24, color: "green" }}>
                          <CheckCircleIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={event.status.replace("_", " ")}
                          secondary={new Date(
                            event.timestamp
                          ).toLocaleTimeString()}
                          primaryTypographyProps={{
                            variant: "body2",
                            fontWeight: "medium",
                          }}
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>

              {order.notes && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  Note: {order.notes}
                </Alert>
              )}

              {/* Delivery Images Section */}
              {order.status === "delivered" && (
                <Box sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <PhotoLibraryIcon
                      sx={{ fontSize: 18, color: "primary.main" }}
                    />
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      color="text.primary"
                    >
                      Delivery Images
                    </Typography>
                    <Badge
                      badgeContent={DEMO_DELIVERY_IMAGES.length}
                      color="primary"
                      sx={{
                        ml: 1,
                        "& .MuiBadge-badge": {
                          fontSize: "0.65rem",
                          height: "18px",
                          minWidth: "18px",
                        },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {DEMO_DELIVERY_IMAGES.map((image, imgIndex) => (
                      <Box
                        key={imgIndex}
                        sx={{
                          width: 120,
                          height: 100,
                          cursor: "pointer",
                          borderRadius: "8px",
                          overflow: "hidden",
                          border: "2px solid #e0e0e0",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                            borderColor: "primary.main",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          },
                        }}
                        onClick={() => handleImageClick(imgIndex)}
                      >
                        <img
                          src={image}
                          alt={`Delivery proof ${imgIndex + 1}`}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        )}

        {expanded && (
          <Box sx={{ mt: 1, textAlign: "right" }}>
            <IconButton
              size="small"
              onClick={toggleDetails}
              sx={{
                transform: showDetails ? "rotate(180deg)" : "none",
                transition: "transform 0.3s ease-in-out",
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Image Viewer Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={handleCloseModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "rgba(0, 0, 0, 0.95)",
            borderRadius: "12px",
          },
        }}
      >
        <IconButton
          onClick={handleCloseModal}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            bgcolor: "rgba(255, 255, 255, 0.1)",
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.2)",
            },
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          sx={{
            p: 0,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
          }}
        >
          {DEMO_DELIVERY_IMAGES.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevImage}
                sx={{
                  position: "absolute",
                  left: 16,
                  color: "white",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                  },
                  zIndex: 1,
                }}
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>
              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: "absolute",
                  right: 16,
                  color: "white",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                  },
                  zIndex: 1,
                }}
              >
                <ChevronRightIcon fontSize="large" />
              </IconButton>
            </>
          )}
          <Box
            sx={{
              width: "100%",
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <img
              src={DEMO_DELIVERY_IMAGES[selectedImageIndex]}
              alt={`Delivery proof ${selectedImageIndex + 1}`}
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255, 255, 255, 0.7)",
                mt: 2,
                textAlign: "center",
              }}
            >
              Image {selectedImageIndex + 1} of {DEMO_DELIVERY_IMAGES.length}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

const StopCard = ({ stop, isCompleted, isNext, expandAll }) => {
  // Keep first stop (Thamel) expanded by default
  const [expanded, setExpanded] = useState(
    !isCompleted || stop.stopNumber === 1
  );

  useEffect(() => {
    if (expandAll !== null) {
      setExpanded(expandAll);
    }
  }, [expandAll]);

  const deliveredCount = stop.orders.filter(
    (o) => o.status === "delivered"
  ).length;
  const failedCount = stop.orders.filter((o) => o.status === "failed").length;
  const totalCount = stop.orders.length;

  return (
    <Box sx={{ mb: isCompleted ? 2 : 4, position: "relative" }}>
      {/* Timeline connector line */}
      {!isCompleted && (
        <Box
          sx={{
            position: "absolute",
            left: 24,
            top: 60,
            bottom: 0,
            width: 2,
            backgroundColor: "grey.300",
            zIndex: 0,
          }}
        />
      )}

      <Paper
        elevation={isNext ? 3 : isCompleted ? 1 : 1}
        sx={{
          position: "relative",
          border: isNext
            ? "2px solid #1976d2"
            : isCompleted
            ? "2px solid #e0e0e0"
            : "2px solid #e0e0e0",
          "&:hover": {
            boxShadow: 3,
          },
        }}
      >
        {/* Stop indicator dot */}
        <Box
          sx={{
            position: "absolute",
            left: -12,
            top: 24,
            width: 36,
            height: 36,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            backgroundColor: isCompleted
              ? "#4caf50"
              : isNext
              ? "#1976d2"
              : "#bdbdbd",
            ...(isNext && {
              boxShadow: "0 0 0 4px rgba(25, 118, 210, 0.2)",
            }),
          }}
        >
          <Typography variant="subtitle2" color="white" fontWeight="bold">
            {stop.stopNumber}
          </Typography>
        </Box>

        <CardContent sx={{ pl: 8 }}>
          {/* Stop Header */}
          <Grid
            container
            alignItems="flex-start"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Grid item xs={8}>
              <Grid container alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Grid item>
                  <Typography variant="h6" fontWeight="bold">
                    {stop.location.split(",")[0]}
                  </Typography>
                </Grid>
                <Grid item>
                  <StopStatusChip status={stop.status} />
                </Grid>
              </Grid>
              <Typography
                variant="body2"
                color="text.secondary"
                display="flex"
                alignItems="center"
                gap={1}
              >
                <PlaceIcon fontSize="small" />
                {stop.location}
              </Typography>
              {stop.estimatedArrival && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  ETA: {new Date(stop.estimatedArrival).toLocaleTimeString()}
                </Typography>
              )}
            </Grid>

            <Grid item>
              <IconButton onClick={() => setExpanded(!expanded)} size="small">
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Grid>
          </Grid>

          {/* Orders Summary */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item>
              <Typography variant="body2" color="text.secondary">
                <strong>{totalCount}</strong> order{totalCount !== 1 ? "s" : ""}
              </Typography>
            </Grid>
            {deliveredCount > 0 && (
              <Grid item display="flex" alignItems="center">
                <CheckCircleIcon
                  fontSize="small"
                  color="success"
                  sx={{ mr: 0.5 }}
                />
                <Typography variant="body2" color="success.main">
                  {deliveredCount} delivered
                </Typography>
              </Grid>
            )}
            {failedCount > 0 && (
              <Grid item display="flex" alignItems="center">
                <CancelIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="error.main">
                  {failedCount} failed
                </Typography>
              </Grid>
            )}
          </Grid>

          {/* Orders List */}
          {expanded && (
            <Box>
              {stop.orders.map((order) => (
                <OrderItem
                  key={order.orderId}
                  order={order}
                  expanded={!isCompleted || stop.stopNumber === 1}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Paper>
    </Box>
  );
};

const MetroRouteProgress = ({ stops, currentStopNumber }) => {
  // Add Warehouse as the first stop
  const warehouseStop = {
    stopId: "WAREHOUSE",
    stopNumber: 0,
    location: "Warehouse",
    status: "completed",
  };

  // Only show first 10 stops (plus warehouse)
  const visibleStops = [warehouseStop, ...stops.slice(0, 9)];

  // Find the first pending stop index to show "Next" chip (accounting for warehouse at index 0)
  const firstPendingIndex = visibleStops.findIndex(
    (s) => s.status === "pending"
  );

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Route Progress
      </Typography>
      <Box sx={{ overflowX: "auto", pt: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: "max-content",
            pb: 2,
            minHeight: 140,
          }}
        >
          {visibleStops.map((stop, idx) => {
            const isWarehouse = stop.stopId === "WAREHOUSE";
            const isCompleted = stop.status === "completed";
            const isPending = stop.status === "pending";
            const isNext = isPending && idx === firstPendingIndex;

            return (
              <React.Fragment key={stop.stopId}>
                {/* Stop Node */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {/* Stop Circle */}
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.3s",
                      backgroundColor: isWarehouse
                        ? "#ff9800"
                        : isCompleted
                        ? "#4caf50"
                        : isNext
                        ? "#1976d2"
                        : "#bdbdbd",
                      boxShadow: isNext
                        ? "0 0 0 4px rgba(25, 118, 210, 0.2)"
                        : "none",
                      transform: isNext ? "scale(1.1)" : "scale(1)",
                      ...(isCompleted && {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }),
                    }}
                  >
                    {isWarehouse ? (
                      <WarehouseIcon sx={{ color: "white", fontSize: 24 }} />
                    ) : isCompleted ? (
                      <CheckCircleIcon sx={{ color: "white", fontSize: 24 }} />
                    ) : (
                      <Typography
                        variant="subtitle2"
                        color="white"
                        fontWeight="bold"
                      >
                        {stop.stopNumber}
                      </Typography>
                    )}
                  </Box>

                  {/* Stop Name */}
                  <Box
                    sx={{
                      mt: 1,
                      textAlign: "center",
                      width: 100,
                      minHeight: 52,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight="medium"
                      color={
                        isWarehouse
                          ? "warning.main"
                          : isCompleted
                          ? "success.main"
                          : isNext
                          ? "primary.main"
                          : "text.secondary"
                      }
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      {stop.location.split(",")[0]}
                    </Typography>
                    {isNext && (
                      <Chip
                        label="Next"
                        size="small"
                        color="primary"
                        variant="filled"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Connecting Line */}
                {idx < visibleStops.length - 1 && (
                  <Box
                    sx={{
                      height: 2,
                      transition: "all 0.3s",
                      backgroundColor: isWarehouse
                        ? "#ff9800"
                        : isCompleted
                        ? "#4caf50"
                        : "#bdbdbd",
                      width: 60,
                      mb: 6,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      {/* Legend */}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              backgroundColor: "#4caf50",
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Completed
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              backgroundColor: "#1976d2",
              boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Next Stop
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              backgroundColor: "#bdbdbd",
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Upcoming
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default function DeliveryTrackerAdmin() {
  const [data, setData] = useState(generateMockData());
  const [isPolling, setIsPolling] = useState(false);
  const [expandAll, setExpandAll] = useState(null);

  const handleExpandAll = () => {
    setExpandAll(true);
    setTimeout(() => setExpandAll(null), 100);
  };

  const handleCollapseAll = () => {
    setExpandAll(false);
    setTimeout(() => setExpandAll(null), 100);
  };

  useEffect(() => {
    if (!isPolling) return;

    const interval = setInterval(() => {
      setData((prevData) => {
        const currentStopIndex = prevData.stops.findIndex(
          (s) => s.status === "arrived" || s.status === "en_route"
        );

        if (
          currentStopIndex === -1 ||
          currentStopIndex >= prevData.stops.length - 1
        ) {
          return prevData;
        }

        // Simulate progressing to next stop
        return generateMockData(currentStopIndex);
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [isPolling]);

  const progressPercent = (data.completedStops / data.totalStops) * 100;
  const currentStopData = data.stops.find((s) => s.status === "pending");
  const nextStopData = currentStopData
    ? data.stops[currentStopData.stopNumber]
    : null;

  return (
    <Box sx={{ p: 3, minHeight: 600, width: "100%" }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            Delivery Route Tracker
          </Typography>
          {/* <Button
            variant="contained"
            onClick={() => setIsPolling(!isPolling)}
            startIcon={isPolling ? <StopIcon /> : <PlayArrowIcon />}
            color={isPolling ? "error" : "success"}
          >
            {isPolling ? "Stop Polling" : "Start Polling"}
          </Button> */}
        </Box>

        {/* Driver Info */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
                height: "100%",
                width: "100%",
              }}
            >
              <Avatar sx={{ bgcolor: "primary.light", color: "primary.main" }}>
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Driver
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {data.driverName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {data.driverPhone}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
                height: "100%",
                width: "100%",
              }}
            >
              <Avatar
                sx={{ bgcolor: "secondary.light", color: "secondary.main" }}
              >
                <DirectionsCarIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Vehicle
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {data.vehicleId}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
                height: "100%",
                width: "100%",
              }}
            >
              <Avatar sx={{ bgcolor: "success.light", color: "success.main" }}>
                <PlaceIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Stops Progress
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {data.completedStops} / {data.totalStops} stops
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
                height: "100%",
                width: "100%",
              }}
            >
              <Avatar sx={{ bgcolor: "warning.light", color: "warning.main" }}>
                <LocalShippingIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Orders Delivered
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {data.deliveredOrders} / {data.totalOrders} orders
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Departure Information */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mt: 3,
            mb: 3,
            backgroundColor: "info.lighter",
            borderColor: "info.main",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: "info.main" }}>
              <AccessTimeIcon />
            </Avatar>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="medium"
              >
                Departed from Warehouse
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {new Date(data.routeStartTime).toLocaleDateString("en-US", {
                  weekday: "short",
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}{" "}
                at{" "}
                {new Date(data.routeStartTime).toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Progress Bar */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              fontWeight="medium"
              color="text.secondary"
            >
              Route Progress
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {Math.round(progressPercent)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: "grey.200",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "success.main",
                borderRadius: 4,
              },
            }}
          />
        </Box>

        {/* Next Stop Info */}
        {currentStopData && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mt: 2,
              backgroundColor: "primary.light",
              borderColor: "primary.main",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <LocationOnIcon color="primary" />
              <Typography
                variant="body1"
                fontWeight="medium"
                color="primary.main"
              >
                Next Stop: {currentStopData.stopNumber} of {data.totalStops}
              </Typography>
            </Box>
            <Typography variant="body2" color="primary.dark">
              {currentStopData.location}
            </Typography>
          </Paper>
        )}
      </Paper>

      {/* Metro-Style Route Progress */}
      <MetroRouteProgress
        stops={data.stops}
        currentStopNumber={data.currentStop}
      />

      {/* Route Timeline */}
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Delivery Route
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleExpandAll}
              startIcon={<ExpandMoreIcon />}
            >
              Expand All
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleCollapseAll}
              startIcon={<ExpandLessIcon />}
            >
              Collapse All
            </Button>
          </Box>
        </Box>
        <Box sx={{ pl: 2 }}>
          {data.stops.map((stop, idx) => {
            const firstPendingIdx = data.stops.findIndex(
              (s) => s.status === "pending"
            );
            return (
              <StopCard
                key={stop.stopId}
                stop={stop}
                isCompleted={stop.status === "completed"}
                isNext={stop.status === "pending" && idx === firstPendingIdx}
                expandAll={expandAll}
              />
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
}
